import { Button } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import CustomModal from "../../../../components/common/CustomModal";
import { toast } from "../../../../helpers/toast";
import { stringToArray } from "../../../../utils/array";
import { sliceString } from "../../../../utils/string";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import {
  bulkCreatePOAsync,
  getAllSupplierAsync,
} from "../../../../store/features/NP/supplier/supplierAPI";
import { SupplierGetAllParams } from "../../../../types/redux/NP/supplier";

type Props = {
  open: boolean;
  handleClose: () => void;
  data?: any;
  reRender: (val: boolean) => void;
  keyword?: string;
};

export default function ImportExcel({
  keyword = "",
  open,
  handleClose,
  data,
  reRender,
}: Props) {
  const [fileName, setFileName] = useState("No file chosen");
  const [loading, setLoading] = useState(false);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const { allSupplier, postLoading } = useAppSelector(
    (state) => state.supplier,
  );
  const { login } = useAppSelector((state) => state.login);

  const supplierData = useCallback(() => {
    const params: SupplierGetAllParams = {
      // limit: limit,
      // skip: limit * (page - 1),
    };

    const searchParams: SupplierGetAllParams = {
      supplierName: keyword,
    };

    if (login?.role === "region" || login?.role === "finance") {
      params.region = login?.region;
      searchParams.region = login?.region;
    }

    dispatch(
      getAllSupplierAsync({
        params: keyword ? searchParams : params,
      }),
    );
  }, [dispatch, keyword, login?.region, login?.role]);

  useEffect(() => {
    supplierData();
  }, [supplierData]);

  const handleUpload = async () => {
    const batchSize = 50;

    for (let i = 0; i < uploadedData.length; i += batchSize) {
      const batch = {
        pos: [...uploadedData.slice(i, i + batchSize)],
      };

      dispatch(
        bulkCreatePOAsync({
          data: batch,
          others: {
            reRender,
          },
        }),
      );

      // console.log("batch: ", batch);
    }
  };

  const handleUploadFile = (file: any) => {
    if (
      file?.name?.endsWith(".xls") ||
      file?.name?.endsWith(".xlsx") ||
      file?.type === "application/vnd.ms-excel" ||
      file?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setLoading(true);
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      const fileNameArr = stringToArray(file?.name, ".");
      setFileName(
        `${sliceString(file?.name, 30, "...")}.${
          fileNameArr?.[fileNameArr?.length - 1]
        }`,
      );

      reader.onload = (e) => {
        const data = e?.target?.result;
        const workBook = XLSX.read(data, {
          type: "binary",
          cellDates: true,
          dateNF: "mm/dd/yyyy",
        });

        const sheetName = workBook.SheetNames[0];
        const sheet = workBook.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json(sheet, { raw: false });
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let hasError = false;
        const validatedData: any[] = [];

        rawData.forEach((el: any, index: number) => {
          const issueDateRaw =
            el?.["POIssuanceDate"]?.trim() ||
            el?.["PO Issue Date:"]?.trim() ||
            "";
          const expireDateRaw =
            el?.["poExpireDate"]?.trim() ||
            el?.["PO Expire Date:"]?.trim() ||
            "";

          const pgValidTTQty =
            el?.["pgValidTTQty"]?.trim() ||
            el?.["PG_Valid TT QTY"]?.trim() ||
            "";

          const pgValidTTIOneUnitPrice =
            (el?.["pgValidTTIOneUnitPrice"] &&
              el["pgValidTTIOneUnitPrice"]
                .toString()
                .trim()
                .replace(/,/g, "")) ||
            (el?.["PG_Valid TT Unit Price [BDT]"] &&
              el["PG_Valid TT Unit Price [BDT]"]
                .toString()
                .trim()
                .replace(/,/g, "")) ||
            "";

          // const totalAmount =
          //   (el?.["totalAmount"] &&
          //     el["totalAmount"]
          //       .toString()
          //       .trim()
          //       .replace(/,/g, "")
          //       .split(".")[0]) ||
          //   (el?.["Total Amount"] &&
          //     el["Total Amount"]
          //       .toString()
          //       .trim()
          //       .replace(/,/g, "")
          //       .split(".")[0]) ||
          //   "";
          const totalAmount =
            (el?.["totalAmount"] &&
              el["totalAmount"].toString().trim().replace(/,/g, "")) ||
            (el?.["Total Amount"] &&
              el["Total Amount"].toString().trim().replace(/,/g, "")) ||
            "";

          // const vat =
          //   (el?.["vat"] &&
          //     el["vat"].toString().trim().replace(/,/g, "").split(".")[0]) ||
          //   (el?.["VAT"] &&
          //     el["VAT"].toString().trim().replace(/,/g, "").split(".")[0]) ||
          //   "";

          const vat =
            (el?.["vat"] && el["vat"].toString().trim().replace(/,/g, "")) ||
            (el?.["VAT"] && el["VAT"].toString().trim().replace(/,/g, "")) ||
            "";

          // const totalWithVat =
          //   (el?.["totalWithVat"] &&
          //     el["totalWithVat"]
          //       .toString()
          //       .trim()
          //       .replace(/,/g, "")
          //       .split(".")[0]) ||
          //   (el?.["Total with VAT"] &&
          //     el["Total with VAT"]
          //       .toString()
          //       .trim()
          //       .replace(/,/g, "")
          //       .split(".")[0]) ||
          //   "";

          const totalAmountNum = parseFloat(totalAmount) || 0;
          const vatNum =
            totalAmount && vat ? (totalAmountNum * parseFloat(vat)) / 100 : 0;
          const totalWithVat = (totalAmountNum + vatNum).toFixed(2);

          const issueDate = issueDateRaw ? new Date(issueDateRaw) : null;
          const expireDate = expireDateRaw ? new Date(expireDateRaw) : null;

          // Start Validations (keep as-is)
          if (issueDate && expireDate && issueDate > expireDate) {
            toast(
              "error",
              `Row ${
                index + 1
              }: PO Issue Date cannot be later than PO Expire Date`,
            );
            hasError = true;
            return;
          }

          if (issueDate && issueDate < today) {
            toast(
              "error",
              `Row ${index + 1}: PO Issue Date cannot be a past date`,
            );
            hasError = true;
            return;
          }

          if (expireDate && expireDate < today) {
            toast(
              "error",
              `Row ${index + 1}: PO Expire Date cannot be a past date`,
            );
            hasError = true;
            return;
          }

          if (parseFloat(pgValidTTQty) < 0) {
            toast(
              "error",
              `Row ${index + 1}: PG_Valid TT QTY cannot be negative`,
            );
            hasError = true;
            return;
          }
          if (pgValidTTQty && !/^\d+(\.\d+)?$/.test(pgValidTTQty)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: PG_Valid TT QTY can never be Character and Spacial Character`,
            );
            hasError = true;
            return;
          }
          if (pgValidTTQty && !/^\d+$/.test(pgValidTTQty)) {
            toast(
              "error",
              `Row ${index + 1}: PG_Valid TT QTY must be an integer`,
            );
            hasError = true;
            return;
          }

          if (parseFloat(pgValidTTIOneUnitPrice) < 0) {
            toast(
              "error",
              `Row ${
                index + 1
              }: PG_Valid TT Unit Price [BDT] can never be negative`,
            );
            hasError = true;
            return;
          }

          if (pgValidTTQty && !/^\d+(\.\d{1,2})?$/.test(pgValidTTQty)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: The PG_Valid TT QTY field must always allow only up to 2 digits after the decimal point`,
            );
            hasError = true;
            return;
          }
          if (!/^[0-9.]+$/.test(pgValidTTIOneUnitPrice)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: PG_Valid TT Unit Price [BDT] can never be Character and Spacial Character`,
            );
            hasError = true;
            return;
          }
          if (pgValidTTIOneUnitPrice) {
            if (!/^\d+(\.\d{1,2})?$/.test(pgValidTTIOneUnitPrice)) {
              toast(
                "error",
                `Row ${
                  index + 1
                }: PG_Valid TT Unit Price [BDT] field must always allow only up to 2 digits after the decimal point`,
              );
              hasError = true;
              return;
            }
          }

          if (parseFloat(totalAmount) < 0) {
            toast(
              "error",
              `Row ${index + 1}: Total Amount can never be negative`,
            );
            hasError = true;
            return;
          }

          // 1️⃣ Check special characters
          if (totalAmount && !/^[0-9.]+$/.test(totalAmount)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: Total Amount can never contain letters or special characters`,
            );
            hasError = true;
            return;
          }

          if (totalAmount && !/^\d+(\.\d{1,2})?$/.test(totalAmount)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: Total Amount must allow only up to 2 digits after the decimal point`,
            );
            hasError = true;
            return;
          }

          if (parseFloat(vat) < 0) {
            toast("error", `Row ${index + 1}: VAT cannot be negative`);
            hasError = true;
            return;
          }

          // if (vat && !/^\d+(\.\d{1,2})?$/.test(vat)) {
          //   toast(
          //     "error",
          //     `Row ${
          //       index + 1
          //     }: VAT can never be a character or special character`,
          //   );
          //   hasError = true;
          //   return;
          // }

          // if (vat && !/^\d+(\.\d{1,2})?$/.test(vat)) {
          //   toast(
          //     "error",
          //     `Row ${
          //       index + 1
          //     }: VAT field must always allow only up to 2 digits after the decimal point`,
          //   );
          //   hasError = true;
          //   return;
          // }

          if (vat && !/^[0-9.]+$/.test(vat)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: VAT can never contain letters or special characters`,
            );
            hasError = true;
            return;
          }

          if (vat && !/^\d+(\.\d{1,2})?$/.test(vat)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: VAT must allow only up to 2 digits after the decimal point`,
            );
            hasError = true;
            return;
          }

          if (vat && parseFloat(vat) > 100) {
            toast(
              "error",
              `Row ${
                index + 1
              }: VAT cannot exceed 100% under any circumstances`,
            );
            hasError = true;
            return;
          }

          if (totalWithVat && parseFloat(totalWithVat) < 0) {
            toast(
              "error",
              `Row ${index + 1}: Total with VAT can never be negative`,
            );
            hasError = true;
            return;
          }

          if (totalWithVat && !/^\d+(\.\d{1,2})?$/.test(totalWithVat)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: Total with VAT can never be Character and Spacial Character`,
            );
            hasError = true;
            return;
          }

          if (totalWithVat && !/^\d+(\.\d{1,2})?$/.test(totalWithVat)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: Total with VAT field must always allow only up to 2 digits after the decimal point`,
            );
            hasError = true;
            return;
          }

          if (
            totalWithVat &&
            totalAmount &&
            parseFloat(totalWithVat) < parseFloat(totalAmount)
          ) {
            toast(
              "error",
              `Row ${
                index + 1
              }: Total with VAT can never be less than the Total Amount`,
            );
            hasError = true;
            return;
          }

          // Push if valid
          validatedData.push({
            vendorId:
              el?.["vendorId"]?.trim() || el?.["Vendor ID:"]?.trim() || "",
            PONumber:
              el?.["PONumber"]?.trim() || el?.["PO Number:"]?.trim() || "",
            POIssuanceDate: issueDateRaw,
            poExpireDate: expireDateRaw,
            paymentTermDays:
              el?.["paymentTermDays"]?.trim() ||
              el?.["Payment Term [Days]"]?.trim() ||
              "",
            contactName:
              el?.["contactName"]?.trim() || el?.["Contact Name"]?.trim() || "",
            contactNumber:
              el?.["contactNumber"]?.trim() ||
              el?.["Contact Number"]?.trim() ||
              "",
            lineOneItemCode:
              el?.["lineOneItemCode"]?.trim() ||
              el?.["Line 1 Item Code"]?.trim() ||
              "",
            pgValidTTDeliveryTime:
              el?.["pgValidTTDeliveryTime"]?.trim() ||
              el?.["PG_Valid TT Delivery Lead Time"]?.trim() ||
              "",
            pgValidTTQty: pgValidTTQty,
            pgValidTTUOM:
              el?.["pgValidTTUOM"]?.trim() ||
              el?.["PG_Valid TT UOM"]?.trim() ||
              "",
            pgValidTTIOneUnitPrice: pgValidTTIOneUnitPrice,
            lineTwoItemCode:
              el?.["lineTwoItemCode"]?.trim() ||
              el?.["Line 2 Item Code"]?.trim() ||
              "",
            pgRunDeliveryTIme:
              el?.["pgRunDeliveryTIme"]?.trim() ||
              el?.["PG_PGRH Delivery Lead Time"]?.trim() ||
              "",
            pgHty:
              (el?.["pgHty"] &&
                el["pgHty"]
                  .toString()
                  .trim()
                  .replace(/,/g, "")
                  .split(".")[0]) ||
              (el?.["PG_PGRH QTY"] &&
                el["PG_PGRH QTY"]
                  .toString()
                  .trim()
                  .replace(/,/g, "")
                  .split(".")[0]) ||
              "",
            pgRunUom:
              el?.["pgRunUom"]?.trim() || el?.["PG_PGRH UOM"]?.trim() || "",
            pgRunHourUnitPrice:
              (el?.["pgRunHourUnitPrice"] &&
                el["pgRunHourUnitPrice"]
                  .toString()
                  .trim()
                  .replace(/,/g, "")
                  .split(".")[0]) ||
              (el?.["PG_PGRH Unit Price [BDT]"] &&
                el["PG_PGRH Unit Price [BDT]"]
                  .toString()
                  .trim()
                  .replace(/,/g, "")
                  .split(".")[0]) ||
              "",
            totalAmount: totalAmount,
            vat: vat,
            totalWithVat: totalWithVat,
          });
        });

        if (!hasError) {
          setUploadedData(validatedData);
        } else {
          setUploadedData([]); // or skip this line
        }

        setLoading(false);
      };
    } else {
      setLoading(false);
      toast("error", "Please upload only excel file");
    }
  };

  const handleDownloadExcel = () => {
    if (!allSupplier || allSupplier.length === 0) {
      toast("error", "No supplier data available to download");
      return;
    }

    // ✅ Filter only suppliers with a valid vendorId
    const filteredSuppliers = allSupplier.filter(
      (supplier: any) => supplier?.vendorId && supplier.vendorId.trim() !== "",
    );

    if (filteredSuppliers.length === 0) {
      toast("error", "No valid vendor IDs found");
      return;
    }

    // Prepare data
    const excelData = filteredSuppliers.map((supplier: any) => ({
      "Vendor ID:": supplier.vendorId,
      "PO Number:": "",
      "PO Issue Date:": "",
      "PO Expire Date:": "",
      "Payment Term [Days]": "",
      "Contact Name": "",
      "Contact Number": "",
      "Line 1 Item Code": "",
      "PG_Valid TT Delivery Lead Time": "",
      "PG_Valid TT QTY": "",
      "PG_Valid TT UOM": "",
      "PG_Valid TT Unit Price [BDT]": "",
      "Line 2 Item Code": "",
      "PG_PGRH Delivery Lead Time": "",
      "PG_PGRH QTY": "",
      "PG_PGRH UOM": "",
      "PG_PGRH Unit Price [BDT]": "",
      "Total Amount": "",
      VAT: "",
    }));

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PO Template");

    // Download Excel
    XLSX.writeFile(workbook, "PO_Template.xlsx");
  };

  return (
    <CustomModal
      width={600}
      title="Excel Sheet"
      open={open}
      handleClose={handleClose}
    >
      <div className="p-6">
        <label className="text-[#3d4a5c] text-sm font-medium leading-normal mb-1.5 inline-block">
          Import Excel File
        </label>
        <Dragger
          name="file"
          action=""
          accept=".xlsx, .xls"
          maxCount={1}
          showUploadList={false}
          beforeUpload={() => false}
          onChange={(info) => handleUploadFile(info.file)}
          onDrop={(e) => handleUploadFile(e.dataTransfer.files[0])}
          disabled={loading}
          className="import_excel"
        >
          <Button
            className="flex justify-between items-center"
            size="large"
            disabled={loading}
            style={{ width: "100%" }}
          >
            <div className="flex items-center gap-2 text-sm font-normal font-roboto text-[#8797ae]">
              <span className="text-accent font-medium text-base leading-7 relative">
                Choose File
              </span>
              <span className="border-r border-solid border-l-0 border-y-0 border-others-border h-4" />
              {loading ? "Uploading..." : fileName}
            </div>
          </Button>
        </Dragger>

        <div className="px-4 py-[9px] bg-[#fffbe6] rounded-md border border-[#ffe58f] text-sm text-[#874d00] mt-4">
          <span className="font-medium">Warning! </span>
          <span>
            Upload only xlsx file. For any blank column put N/A, otherwise data
            will be mismatch. Your can see sample file,{" "}
          </span>
          <Button
            className="text-[#1890ff] cursor-pointer text-sm font-medium bg-transparent border-none"
            onClick={handleDownloadExcel}
          >
            click here
          </Button>
        </div>

        <div className="flex justify-center mt-7">
          <Button
            size="large"
            type="primary"
            className="w-72 !h-12"
            onClick={handleUpload}
            disabled={loading || uploadedData.length === 0}
            loading={postLoading}
          >
            Upload
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
